// دیالوگ دعوت کارکن (US-IAM-011، SCR-STAFF-920): موبایل، نام، نقش‌ها (فقط نقش‌های قابل تخصیص — BR-IAM-013) و شعب با شعبه
// پیش‌فرض؛ سقف کاربران ← PHX-PLT-0402 با UpgradeBadge (AC-IAM-011.2)؛ عضو موجود ← PHX-IAM-0201 روی موبایل (AC-IAM-011.4).
import { zodResolver } from '@hookform/resolvers/zod';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import { isApiError, unwrap, type InviteMemberRequest, type InviteMemberResult } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  FormDialog,
  MobileFieldRHF,
  SelectRHF,
  TextFieldRHF,
  zMobile,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  useController,
  useForm,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { z } from 'zod';
import {
  assignableRoles,
  iamKeys,
  LimitReachedAlert,
  useBranchOptions,
  usePermissionCatalog,
  useRoleLabel,
  useRoles,
  type BranchOption,
} from './common';

export const inviteSchema = z
  .object({
    mobile: zMobile(),
    firstName: zRequiredText(100),
    lastName: zRequiredText(100),
    roles: z.array(z.string()).min(1, { error: 'iam:invite.roles_required' }),
    branchIds: z.array(z.string()).min(1, { error: 'iam:invite.branches_required' }),
    defaultBranchId: z.string(),
  })
  .refine((form) => !form.defaultBranchId || form.branchIds.includes(form.defaultBranchId), {
    path: ['defaultBranchId'],
    error: 'errors:validation.branch_not_in_scope',
  });
type InviteForm = z.input<typeof inviteSchema>;

const FIELDS = ['mobile', 'firstName', 'lastName', 'roles', 'branchIds', 'defaultBranchId'] as const;

/** گروه چک‌باکس چندانتخابی متصل به RHF (نقش‌ها و شعب). */
export function CheckboxGroupRHF<T extends FieldValues>({
  control,
  name,
  labelKey,
  options,
  testId,
  disabledValues,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  labelKey: string;
  options: readonly { value: string; label: string }[];
  testId: string;
  disabledValues?: ReadonlySet<string>;
}) {
  const { t } = useTranslation('iam');
  const { field, fieldState } = useController({ control, name });
  const value = Array.isArray(field.value) ? (field.value as string[]) : [];
  const toggle = (option: string, checked: boolean) =>
    field.onChange(checked ? [...value, option] : value.filter((item) => item !== option));
  return (
    <FormControl component="fieldset" error={Boolean(fieldState.error)} data-testid={testId}>
      <FormLabel component="legend">{t(labelKey)}</FormLabel>
      <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            label={option.label}
            disabled={disabledValues?.has(option.value)}
            control={
              <Checkbox
                checked={value.includes(option.value)}
                onChange={(event) => toggle(option.value, event.target.checked)}
                slotProps={{ input: { 'data-value': option.value } as Record<string, unknown> }}
              />
            }
          />
        ))}
      </FormGroup>
      {fieldState.error?.message && <FormHelperText>{t(fieldState.error.message)}</FormHelperText>}
    </FormControl>
  );
}

function branchOptions(branches: readonly BranchOption[], unnamed: string) {
  return branches.map((branch) => ({ value: branch.id, label: branch.name || unnamed }));
}

export function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (result: InviteMemberResult) => void;
}) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const roleLabel = useRoleLabel();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const { branches } = useBranchOptions();
  const [submitError, setSubmitError] = useState<unknown>();

  const roleOptions = useMemo(
    () =>
      assignableRoles(roles.data?.items ?? [], access.can, catalog.data?.items).map((role) => ({
        value: role.code,
        label: roleLabel(role.code),
      })),
    [roles.data, access.can, catalog.data, roleLabel],
  );

  const defaults = useMemo<InviteForm>(
    () => ({
      mobile: '',
      firstName: '',
      lastName: '',
      roles: [],
      branchIds: branches.length === 1 && branches[0] ? [branches[0].id] : [],
      defaultBranchId: '',
    }),
    [branches],
  );
  const { control, handleSubmit, formState, setError, reset, setValue } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    values: defaults,
  });
  const selectedBranches = useWatch({ control, name: 'branchIds' });
  const defaultBranch = useWatch({ control, name: 'defaultBranchId' });
  useEffect(() => {
    if (defaultBranch && !selectedBranches.includes(defaultBranch)) {
      setValue('defaultBranchId', '');
    }
  }, [defaultBranch, selectedBranches, setValue]);

  const invite = useMutation({
    mutationFn: (body: InviteMemberRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/identity/memberships:invite', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
      access.refetch();
      reset();
      onInvited(result);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: FIELDS,
        codeFields: { 'PHX-IAM-0201': 'mobile', 'PHX-IAM-0203': 'roles' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = handleSubmit((form) =>
    invite.mutate({
      mobile: form.mobile,
      firstName: form.firstName,
      lastName: form.lastName,
      roles: form.roles,
      branchIds: form.branchIds,
      defaultBranchId:
        form.defaultBranchId || (form.branchIds.length === 1 ? (form.branchIds[0] ?? null) : null),
    }),
  );

  const limitError = isApiError(submitError) && submitError.code === 'PHX-PLT-0402';
  const unnamed = t('shell:branch.unnamed');

  return (
    <FormDialog
      open={open}
      titleKey="iam:invite.title"
      screenId="SCR-STAFF-920-INVITE"
      onSubmit={() => void submit()}
      onClose={() => {
        reset();
        setSubmitError(undefined);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={invite.isPending}
      submitError={limitError ? undefined : submitError}
      submitKey="iam:invite.submit"
      maxWidth="md"
    >
      <LimitReachedAlert error={submitError} limitKey="limit.staff_users" hintKey="iam:invite.limit_hint" />
      <MobileFieldRHF control={control} name="mobile" labelKey="iam:invite.mobile" required autoFocus />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="firstName"
          labelKey="iam:invite.first_name"
          required
          fullWidth
        />
        <TextFieldRHF control={control} name="lastName" labelKey="iam:invite.last_name" required fullWidth />
      </Stack>
      <CheckboxGroupRHF
        control={control}
        name="roles"
        labelKey="iam:invite.roles"
        options={roleOptions}
        testId="invite-roles"
      />
      <CheckboxGroupRHF
        control={control}
        name="branchIds"
        labelKey="iam:invite.branches"
        options={branchOptions(branches, unnamed)}
        testId="invite-branches"
      />
      {selectedBranches.length > 1 && (
        <SelectRHF
          control={control}
          name="defaultBranchId"
          labelKey="iam:invite.default_branch"
          helperKey="iam:invite.default_branch_hint"
          options={branchOptions(
            branches.filter((branch) => selectedBranches.includes(branch.id)),
            unnamed,
          )}
        />
      )}
    </FormDialog>
  );
}
